import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../Hooks/DeleteHook";
import { singleImageUpload } from "../../Hooks/ImageUpload";
import UpdateHooks from "../../Hooks/UpdateHooks";

function SuperAdminTeamForm() {
  const [teams, setTeams] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [refetch, setRefetch] = useState(false);
  const [teamData, setTeamData] = useState({
    Name: "",
    description: "",
  });

  const fileInputRef = useRef();

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch("https://servercareseebd.careseebd.com/api/v1/teams");
        const data = await response.json();
        setTeams(data.data || []);
      } catch (error) {
        console.error("Error fetching team data:", error);
      }
    }
    fetchData();
  }, [refetch]);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setTeamData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (teamData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/teams/${teamData?._id}`,
          { ...teamData, imageUrl: imageUrl ? imageUrl : teamData?.imageUrl }
        );
        toast.success("Team member updated successfully!");
        setTeamData({ Name: "", description: "" });
        setImageUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setRefetch(!refetch);
      } else {
        const response = await fetch("https://servercareseebd.careseebd.com/api/v1/teams", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...teamData, imageUrl }),
        });

        const result = await response.json();
        setRefetch(!refetch);
        if (result) {
          toast.success("Team member added successfully!");
          setTeamData({
            Name: "",
            description: "",
          });
          setImageUrl("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Error saving team member:", error);
    }
  };

  const handleChangeUploadImage = async (event) => {
    const image = event.target.files[0];
    const formData = new FormData();
    formData.append("image", image);
    singleImageUpload(formData, setImageUrl);
  };

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:users-group-rounded-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Team Management</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage clinical and administrative team profiles</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 h-fit">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {teamData?._id ? "Edit Team Member" : "Add Team Member"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Photo</label>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={handleChangeUploadImage}
                  ref={fileInputRef}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs file:mr-3 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-[10px] file:font-extrabold file:uppercase file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  accept="image/*"
                />
                {(imageUrl || teamData?.imageUrl) && (
                  <div className="w-16 h-16 rounded-xl border border-slate-100 overflow-hidden bg-slate-50 p-1 flex items-center justify-center">
                    <img className="max-w-full max-h-full object-contain rounded-lg" src={imageUrl || teamData?.imageUrl} alt="preview" />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Full Name</label>
              <input
                type="text"
                name="Name"
                onChange={handleChange}
                value={teamData.Name}
                required
                placeholder="e.g. Dr. John Doe"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Bio / Description</label>
              <textarea
                name="description"
                onChange={handleChange}
                value={teamData.description}
                required
                rows="4"
                placeholder="e.g. Chief Medical Officer with 15+ years of experience"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium"
              />
            </div>

            <div className="flex gap-2">
              {teamData?._id && (
                <button
                  type="button"
                  onClick={() => {
                    setTeamData({ Name: "", description: "" });
                    setImageUrl("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-[11px] transition-all hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-[11px] rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {teamData?._id ? "Update Member" : "Add Member"}
              </button>
            </div>
          </form>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Team Members</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Member</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {teams.length > 0 ? (
                  teams.map((team) => (
                    <tr key={team?._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800">
                        <div className="flex items-center gap-3">
                          {team?.imageUrl ? (
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-50 border border-slate-100 p-0.5 flex items-center justify-center">
                              <img className="max-w-full max-h-full object-cover rounded-full" src={team?.imageUrl} alt={team?.Name} />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 font-extrabold flex items-center justify-center uppercase">
                              {(team?.Name || "T").substring(0, 1)}
                            </div>
                          )}
                          <span>{team?.Name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-xs truncate">
                        {team?.description}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={async () => {
                              setTeamData(team);
                              setImageUrl(team?.imageUrl || "");
                              scrollToTop();
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                            title="Edit Member"
                          >
                            <Icon icon="basil:edit-outline" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              DeleteHook({
                                setRefetch,
                                refetch,
                                url: `https://servercareseebd.careseebd.com/api/v1/teams/${team?._id}`,
                              })
                            }
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete Member"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-10 text-xs text-slate-400">
                      No team members added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminTeamForm;
